/**
 * Allure session guard — distinguishes cold launches from background→foreground
 * resumes so we know when to require biometric unlock.
 *
 * The primary signal is `sessionStorage` — which is per-browsing-context. In a
 * Capacitor WebView, sessionStorage PERSISTS across background/resume cycles
 * when the renderer process stays alive. However, Android OEMs (Samsung,
 * Xiaomi, OPPO, etc.) aggressively kill the renderer to free memory even during
 * brief background intervals. When the renderer is killed, sessionStorage is
 * wiped — causing isFreshLaunch() to return `true` and incorrectly firing the
 * biometric prompt on every background-resume.
 *
 * Secondary signal: a "last active" timestamp in Capacitor Preferences.
 * Preferences survive renderer death (they're in Android SharedPreferences).
 * hydrateAuthFromNative() (called in index.js before React mounts) checks
 * this timestamp and, if it's within SESSION_RESUME_WINDOW_MS, restores the
 * sessionStorage flag — so isFreshLaunch() correctly returns `false` for a
 * renderer-restart-on-resume scenario.
 *
 * Two functions:
 *   - `isFreshLaunch()` → true the very first time it's called per process,
 *     false on every subsequent call within the same browsing context.
 *   - `markSessionAlive()` → idempotent setter called immediately AFTER a
 *     successful unlock or login completes, so subsequent renders skip the
 *     unlock screen until the next cold start.
 */
import { touchSessionAliveNative } from './authStorage';

const KEY = 'allure_session_alive_v1';

export const isFreshLaunch = () => {
  try {
    return window.sessionStorage.getItem(KEY) !== '1';
  } catch {
    // sessionStorage disabled (private mode on some browsers) — fail safe
    // by treating every render as a fresh launch. Worst case the user
    // unlocks more often than necessary; never less.
    return true;
  }
};

export const markSessionAlive = () => {
  try {
    window.sessionStorage.setItem(KEY, '1');
  } catch {
    /* noop — see comment above */
  }
  // Also persist a timestamp to Capacitor Preferences so the session-alive
  // signal survives renderer death (background → foreground on aggressive
  // Android OEMs). hydrateAuthFromNative() in index.js reads this back and
  // restores sessionStorage before React mounts, preventing false cold-launch
  // detection and the resulting biometric-on-every-background-switch bug.
  touchSessionAliveNative(); // fire-and-forget async
};

export const clearSessionAlive = () => {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
};
